import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import heroAsset from "@/assets/hero-v2.jpg.asset.json";
import hero480 from "@/assets/hero-480.webp";
import hero800 from "@/assets/hero-800.webp";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { services, plans } from "@/data/site-content";
import { postSummaries } from "@/data/post-index";
import { CurrencyToggle, useCurrency } from "@/hooks/use-currency";
import { button3DClass } from "@/components/button-3d";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Digital Marketing Agency in India | MG Content Labs" },
      {
        name: "description",
        content:
          "B2B digital marketing agency in India offering SEO content strategy, content writing, SMO, and answer engine optimization. Monthly plans in INR and USD.",
      },
      { property: "og:title", content: "Digital Marketing Agency in India | MG Content Labs" },
      {
        property: "og:description",
        content:
          "A B2B digital marketing agency in India: SEO content strategy, content writing services, SMO, and AEO that make your brand the answer across search and AI.",
      },
      { property: "og:url", content: "https://mgcontentlabs.com/" },
      { property: "og:image", content: `https://mgcontentlabs.com${heroAsset.url}` },
      { name: "twitter:image", content: `https://mgcontentlabs.com${heroAsset.url}` },
    ],
    links: [
      { rel: "canonical", href: "https://mgcontentlabs.com/" },
      {
        rel: "preload",
        as: "image",
        href: hero800,
        imageSrcSet: `${hero480} 480w, ${hero800} 800w`,
        imageSizes: "(min-width: 1024px) 560px, 92vw",
        type: "image/webp",
        fetchpriority: "high",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: homeFaqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Index,
});

const homeFaqs: { q: string; a: string }[] = [
  {
    q: "What does a digital marketing agency do?",
    a: "A digital marketing agency plans, produces, and distributes content that attracts and converts your buyers. At MG Content Labs that means SEO content strategy, content writing, social media optimization, content optimization, answer engine optimization, and link building — measured against rankings, AI citations, and pipeline.",
  },
  {
    q: "Why hire a digital marketing agency instead of building in-house?",
    a: "An agency gives you a senior strategist, writers, and SEO specialists from day one, at a fraction of the cost of hiring the same roles. You also get a repeatable system rather than ad-hoc publishing.",
  },
  {
    q: "How much do our services cost in India?",
    a: "Our plans start at ₹9,990 per month (about $119) for the Baseline plan and ₹17,990 per month (about $215) for the Growth plan, with custom Enterprise scoping available. Full details are on the pricing page.",
  },
  {
    q: "Do you work with B2B and SaaS brands specifically?",
    a: "Yes. B2B and SaaS digital marketing is our core focus — long sales cycles, technical subject matter, and buyers who research before they ever speak to sales.",
  },
  {
    q: "What is answer engine optimization (AEO)?",
    a: "AEO structures your content so AI search engines such as ChatGPT, Gemini, Perplexity, and Google AI Overviews quote it as a source. It sits alongside traditional SEO rather than replacing it.",
  },
];

function Index() {
  const { currency } = useCurrency();
  return (
    <div className="min-h-screen bg-white font-sans text-brand-deep">
      <SiteNav />

      {/* Hero */}
      <header className="relative overflow-hidden bg-brand-muted pt-10 pb-20 lg:pt-14 lg:pb-28">
        {/* Animated background field */}
        <div className="pointer-events-none absolute inset-0 -z-0 hidden md:block" aria-hidden="true">
          <div className="absolute -left-24 -top-24 h-96 w-96 rounded-full bg-brand-accent/25 blur-3xl animate-blob" />
          <div className="absolute -right-32 top-40 h-[28rem] w-[28rem] rounded-full bg-[color:var(--color-brand-glow)]/20 blur-3xl animate-blob-slow" />
          <div className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-brand-deep/10 blur-3xl animate-glow-pulse" />
          <svg className="absolute inset-0 h-full w-full opacity-[0.07]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M40 0H0V40" fill="none" stroke="currentColor" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-accent/30 bg-brand-accent/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-brand-accent">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-accent opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-accent" />
                </span>
                Content for the AI Era
              </span>
              <h1 className="mt-6 font-serif text-5xl leading-[1.05] md:text-7xl">
                B2B digital marketing agency <br />
                <span className="italic text-brand-accent">in India.</span>
              </h1>
              <p className="mt-8 max-w-lg text-lg leading-relaxed text-brand-deep/70">
                MG Content Labs is a Delhi-based digital marketing agency delivering SEO content
                strategy, content writing services, social media optimization, and answer engine
                optimization — so your brand is the definitive answer across Google and AI search.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/pricing"
                  className={button3DClass("primary")}
                >
                  View Growth Plans
                </Link>
                <Link
                  to="/contact"
                  className={button3DClass("secondary")}
                >
                  Book a Strategy Call
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-6 -z-10 rounded-3xl bg-gradient-to-tr from-brand-accent/40 via-[color:var(--color-brand-glow)]/25 to-transparent blur-3xl animate-glow-pulse" />
              <div className="relative overflow-hidden md:animate-float rounded-2xl shadow-2xl outline outline-1 -outline-offset-1 outline-black/5">
                <img
                  src={hero800}
                  srcSet={`${hero480} 480w, ${hero800} 800w`}
                  sizes="(min-width: 1024px) 560px, 92vw"
                  alt="Abstract editorial typographic composition"
                  width={1280}
                  height={1600}
                  loading="eager"
                  fetchPriority="high"
                  className="aspect-[4/5] w-full object-cover"
                />
                {/* Shimmer sweep */}
                <div className="pointer-events-none absolute inset-0 hidden overflow-hidden md:block">
                  <div className="animate-shimmer absolute -inset-y-4 -left-1/2 w-1/3 rotate-12 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                </div>
              </div>

              {/* Floating capability cards */}
              <div
                className="absolute -left-4 top-8 z-20 max-w-[13rem] rounded-xl border border-white/10 bg-brand-deep/90 px-4 py-3 text-white shadow-2xl backdrop-blur-md sm:-left-8"
                style={{ animation: "float-y 6s ease-in-out infinite" }}
              >
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-brand-accent">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-accent animate-glow-pulse" />
                  Search
                </div>
                <div className="mt-1 font-serif text-lg italic leading-tight">SEO content strategy</div>
                <div className="mt-0.5 text-[10px] text-white/60">topical maps, not one-off posts</div>
              </div>

              <div
                className="absolute right-0 top-24 z-20 max-w-[13rem] rounded-xl border border-brand-accent/30 bg-white/95 px-4 py-3 text-brand-deep shadow-2xl backdrop-blur sm:-right-6"
                style={{ animation: "float-y 7s ease-in-out infinite 0.8s" }}
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">AI Search</div>
                <div className="mt-1 font-serif text-lg italic leading-tight">Built to be cited</div>
                <div className="mt-0.5 text-[10px] text-brand-deep/60">ChatGPT · Gemini · AI Overviews</div>
              </div>

              <div
                className="absolute -left-6 bottom-24 z-20 hidden max-w-[13rem] rounded-xl border border-brand-deep/10 bg-[color:var(--color-brand-cream)] px-4 py-3 text-brand-deep shadow-2xl sm:block"
                style={{ animation: "float-y 8s ease-in-out infinite 1.4s" }}
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">Editorial</div>
                <div className="mt-1 font-serif text-lg italic leading-tight">Written by specialists</div>
                <div className="mt-0.5 text-[10px] text-brand-deep/60">no thin, templated drafts</div>
              </div>

              <div
                className="absolute -right-4 bottom-8 z-20 max-w-[13rem] rounded-xl border border-white/10 bg-brand-ink/90 px-4 py-3 text-white shadow-2xl backdrop-blur-md sm:-right-10"
                style={{ animation: "float-y 9s ease-in-out infinite 0.4s" }}
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--color-brand-glow)]">
                  Reporting
                </div>
                <div className="mt-1 font-serif text-lg italic leading-tight">Tied to pipeline</div>
                <div className="mt-0.5 text-[10px] text-white/60">rankings, citations, leads</div>
              </div>

            </div>
          </div>
        </div>
      </header>

      {/* Focus bar */}
      <section className="overflow-hidden border-b border-brand-deep/5 py-10">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-6 text-center text-[10px] font-bold uppercase tracking-[0.2em] text-brand-deep/60">
            Built for B2B, SaaS and service brands
          </div>
          <div className="relative flex overflow-hidden">
            <div className="flex shrink-0 animate-marquee gap-16 pr-16 opacity-70">
              {[...Array(2)].flatMap((_, i) =>
                [
                  "SEO CONTENT STRATEGY",
                  "CONTENT WRITING",
                  "ANSWER ENGINE OPTIMIZATION",
                  "SOCIAL MEDIA OPTIMIZATION",
                  "CONTENT OPTIMIZATION",
                  "LINK BUILDING",
                ].map((b) => (
                  <span key={`${b}-${i}`} className="font-serif text-lg font-bold tracking-tight text-brand-deep">
                    {b}
                  </span>
                )),
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-16 flex flex-wrap items-end justify-between gap-6">
            <div>
              <h2 className="font-serif text-4xl">Our services</h2>
              <div className="mt-4 h-1 w-20 bg-brand-accent" />
            </div>
            <Link to="/services" className="text-sm font-semibold text-brand-accent hover:text-brand-deep">
              Explore all services →
            </Link>
          </div>
          <div className="grid gap-x-8 gap-y-12 md:grid-cols-3">
            {services.slice(0, 6).map((s) => (
              <Link
                key={s.slug}
                to="/services/$slug"
                params={{ slug: s.slug }}
                className="group block border-t border-brand-deep/10 pt-8"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">
                  {s.eyebrow}
                </div>
                <h3 className="mt-3 text-xl font-bold transition-colors group-hover:text-brand-accent">
                  {s.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-brand-deep/60">{s.summary}</p>
                <span className="mt-4 inline-block text-xs font-semibold text-brand-accent">
                  Know more →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-brand-muted py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-16 max-w-2xl">
            <h2 className="font-serif text-4xl md:text-5xl">What founders and marketing leaders say</h2>
            <p className="mt-4 text-brand-deep/70">
              Real feedback from teams we have worked with across SaaS, B2B services, and tech-enabled brands.
            </p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                quote:
                  "MG Content Labs helped us move from ad-hoc blog posts to a real content engine. Their editorial calendar and research process finally made our content predictable.",
                name: "Priya Narayan",
                role: "Marketing Lead, B2B SaaS company",
              },
              {
                quote:
                  "They understand B2B buying cycles. The long-form pieces they produce are now part of our sales conversations — prospects reference them before we even get on a call.",
                name: "Arjun Mehta",
                role: "Founder, fintech startup",
              },
              {
                quote:
                  "Our organic traffic turned into a genuine demand channel instead of a vanity metric. The weekly updates and clear reporting keep the whole team aligned.",
                name: "Sarah Mitchell",
                role: "Head of Demand Generation, SaaS company",
              },
              {
                quote:
                  "Finally, content that sounds like us. The voice guide and SME interviews made the handoff effortless, and the drafts needed far fewer revisions than previous agencies.",
                name: "James Chen",
                role: "CEO, HR-tech platform",
              },
              {
                quote:
                  "Their SEO strategy was not just keyword chasing. They mapped topics around our product category and we started seeing our pages referenced in AI search answers.",
                name: "Emma Larsson",
                role: "Co-founder, devtools company",
              },
              {
                quote:
                  "Working with them feels like having an in-house strategist without the overhead. They ask sharp questions, ship fast, and care about outcomes.",
                name: "Ananya Kulkarni",
                role: "CMO, B2B marketplace",
              },
            ].map((t, i) => (
              <div
                key={t.name}
                className={`group relative overflow-hidden border border-brand-deep/10 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${i === 4 ? "lg:col-start-2" : ""} ${i === 5 ? "lg:col-start-3" : ""}`}
                style={{ animation: `fade-in 0.5s ease-out ${i * 0.08}s both` }}
              >
                {/* Accent line */}
                <div className="absolute left-8 top-0 h-1.5 w-12 bg-brand-accent transition-all duration-300 group-hover:w-16" />

                {/* Quote mark */}
                <div className="mb-6">
                  <svg
                    className="h-8 w-8 text-brand-accent/20 transition-transform duration-300 group-hover:scale-110"
                    fill="currentColor"
                    viewBox="0 0 32 32"
                    aria-hidden="true"
                  >
                    <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                  </svg>
                </div>

                {/* Quote */}
                <p className="text-xl leading-relaxed text-brand-deep">
                  “{t.quote}”
                </p>

                {/* Attribution */}
                <div className="mt-8 flex items-center gap-4 border-t border-brand-deep/10 pt-6">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-accent/10 text-sm font-bold text-brand-accent transition-transform duration-300 group-hover:scale-105">
                    {t.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </div>
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-brand-deep transition-colors group-hover:text-brand-accent">
                      {t.name}
                    </h3>
                    <p className="mt-0.5 text-xs text-brand-deep/60">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-brand-deep py-24 text-white">

        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center">
            <h2 className="font-serif text-4xl md:text-5xl">Our pricing plans</h2>
            <p className="mt-4 text-white/60">
              Transparent monthly digital marketing plans in INR and USD, for every stage of growth.
            </p>
            <div className="mt-8 flex justify-center">
              <CurrencyToggle tone="dark" />
            </div>
          </div>
          <div className="mt-20 grid gap-8 lg:grid-cols-3">
            {plans.map((plan) => (
              <div
                key={plan.slug}
                className={
                  plan.featured
                    ? "relative flex flex-col border border-brand-accent bg-white p-10 text-brand-deep shadow-2xl lg:scale-105"
                    : "flex flex-col border border-white/10 bg-white/5 p-10"
                }
              >
                {plan.featured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-brand-accent px-4 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                    Recommended
                  </div>
                )}
                <div className="mb-8">
                  <h3 className="text-lg font-bold uppercase tracking-widest">{plan.name}</h3>
                  <div className="mt-4 flex items-baseline">
                    <span className="text-4xl font-bold italic tabular-nums">
                      {currency === "USD" ? plan.price.usd : plan.price.inr}
                    </span>
                    {plan.cadence && (
                      <span className={plan.featured ? "ml-2 text-brand-deep/80" : "ml-2 text-white/80"}>
                        {plan.cadence}
                      </span>
                    )}
                  </div>
                </div>
                <ul className={plan.featured ? "mb-10 flex-1 space-y-4 text-sm text-brand-deep/70" : "mb-10 flex-1 space-y-4 text-sm text-white/70"}>
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className="mt-1 text-brand-accent">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/pricing"
                  hash={plan.slug}
                  className={
                    plan.featured
                      ? button3DClass("accent", "default", true)
                      : button3DClass("outlineWhite", "default", true)
                  }
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-12 flex items-end justify-between">
            <h2 className="font-serif text-3xl">Digital marketing and SEO insights</h2>
            <Link to="/blog" className="text-sm font-semibold text-brand-accent hover:text-brand-deep">
              Read all blogs →
            </Link>

          </div>
          <div className="grid gap-10 md:grid-cols-3">
            {postSummaries.slice(0, 3).map((p) => (
              <Link
                to="/blog/$slug"
                params={{ slug: p.slug }}
                key={p.slug}
                className="group block"
              >
                <img
                  src={p.cover}
                  alt={p.title}
                  width={1200}
                  height={800}
                  loading="lazy"
                  className="aspect-[3/2] w-full object-cover outline outline-1 -outline-offset-1 outline-black/5"
                />
                <time className="mt-6 block text-xs font-bold uppercase tracking-widest text-brand-accent">
                  {p.category} · {new Date(p.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </time>
                <h3 className="mt-2 text-lg font-bold leading-snug transition-colors group-hover:text-brand-accent">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm text-brand-deep/60">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24">
        <div className="mx-auto max-w-4xl px-6">
          <h2 className="font-serif text-4xl">FAQs</h2>
          <div className="mt-12 divide-y divide-brand-deep/10">
            {homeFaqs.map((f) => (
              <div key={f.q} className="py-6">
                <h3 className="font-bold">{f.q}</h3>
                <p className="mt-2 text-sm text-brand-deep/70">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-brand-muted py-24">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl">
            Ready to become the <span className="italic text-brand-accent">definitive answer?</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-brand-deep/70">
            Book a 30-minute strategy call. We'll audit your current content engine and map three
            growth plays you can ship this quarter.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className={button3DClass("primary")}>
              Book a Strategy Call
            </Link>
            <Link to="/pricing" className={button3DClass("secondary")}>
              Compare Plans
            </Link>
          </div>
        </div>
      </section>

      {/* What working with us looks like */}
      <section className="border-t border-brand-deep/10 py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: "Led by", value: "A senior strategist", note: "4.5+ years in SEO and content" },
              { label: "Cadence", value: "Weekly shipping", note: "a predictable editorial calendar" },
              { label: "Based in", value: "Delhi, India", note: "working with clients worldwide" },
              { label: "Commitment", value: "Monthly plans", note: "cancel with 30 days' notice" },
            ].map((item) => (
              <div key={item.label} className="text-center">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-deep/60">
                  {item.label}
                </div>
                <div className="mt-2 font-serif text-2xl italic text-brand-accent">{item.value}</div>
                <div className="mt-1 text-xs text-brand-deep/60">{item.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>


      <SiteFooter />
    </div>
  );
}
